import { API } from '@/core/Api';

// Recraft.ai 이미지 편집 서비스
export class RecraftAI {
  
  /**
   * 이미지 배경 제거
   * @param imageFile 이미지 파일
   * @param options 추가 옵션들
   * @returns 배경이 제거된 이미지 URL
   */
  async removeBackground(
    imageFile: File,
    options: {
      response_format?: 'url' | 'b64_json';
    } = {}
  ) {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('response_format', options.response_format || 'url');

    const response = await API.post('/api/ai/recraft/remove-background', formData);
    
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '배경 제거에 실패했습니다.');
    }
  }

  /**
   * 창작적 업스케일
   * @param imageFile 이미지 파일
   * @param options 추가 옵션들
   * @returns 업스케일된 이미지 URL
   */
  async creativeUpscale(
    imageFile: File,
    options: {
      response_format?: 'url' | 'b64_json';
    } = {}
  ) {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('response_format', options.response_format || 'url');

    const response = await API.post('/api/ai/recraft/creative-upscale', formData);
    
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '창작적 업스케일에 실패했습니다.');
    }
  }

  /**
   * 배경 교체
   * @param imageFile 이미지 파일
   * @param prompt 새로운 배경 설명
   * @param options 추가 옵션들
   * @returns 배경이 교체된 이미지 URL
   */
  async replaceBackground(
    imageFile: File,
    prompt: string,
    options: {
      response_format?: 'url' | 'b64_json';
    } = {}
  ) {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('prompt', prompt);
    formData.append('response_format', options.response_format || 'url');

    const response = await API.post('/api/ai/recraft/replace-background', formData);
    
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '배경 교체에 실패했습니다.');
    }
  }

  /**
   * 스타일 생성
   * @param referenceFiles 참조 이미지 파일들 (1-4개)
   * @param options 스타일 옵션들
   * @returns 생성된 스타일 정보
   */
  async createStyle(
    referenceFiles: File[],
    options: {
      style?: string;      // 기본 스타일 (예: 'digital_illustration')
      style_text?: string; // 스타일 설명 (30자 이하)
    } = {}
  ) {
    if (referenceFiles.length === 0 || referenceFiles.length > 4) {
      throw new Error('참조 이미지는 1-4개 사이여야 합니다.');
    }

    const formData = new FormData();
    
    // 여러 파일 추가
    referenceFiles.forEach(file => {
      formData.append('files', file);
    });
    
    if (options.style) {
      formData.append('style', options.style);
    }
    
    if (options.style_text) {
      if (options.style_text.length > 30) {
        throw new Error('스타일 텍스트는 30자 이하여야 합니다.');
      }
      formData.append('style_text', options.style_text);
    }

    const response = await API.post('/api/ai/recraft/create-style', formData);
    
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '스타일 생성에 실패했습니다.');
    }
  }

  /**
   * 이미지 인페인팅 (부분 편집)
   * @param imageFile 원본 이미지 파일
   * @param maskFile 마스크 이미지 파일 (그레이스케일)
   * @param prompt 인페인팅할 내용 설명
   * @param options 추가 옵션들
   * @returns 인페인팅된 이미지 URL
   */
  async imageInpainting(
    imageFile: File,
    maskFile: File,
    prompt: string,
    options: {
      response_format?: 'url' | 'b64_json';
    } = {}
  ) {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('mask', maskFile);
    formData.append('prompt', prompt);
    formData.append('response_format', options.response_format || 'url');

    const response = await API.post('/api/ai/recraft/inpaint', formData);
    
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지 인페인팅에 실패했습니다.');
    }
  }

  /**
   * 이미지-투-이미지 변환
   * @param imageFile 원본 이미지 파일
   * @param prompt 변환 설명
   * @param strength 변환 강도 (0-1)
   * @param options 추가 옵션들
   * @returns 변환된 이미지 URL
   */
  async imageToImage(
    imageFile: File,
    prompt: string,
    strength: number,
    options: {
      styleImages?: File[];      // 스타일 참조 이미지들 (최대 5개)
      style?: string;            // 기본 스타일
      styleId?: string;          // 기존 스타일 ID
      n?: number;                // 생성할 이미지 수 (1-6)
      model?: string;            // 모델 (recraftv3, recraftv2)
      negativePrompt?: string;   // 부정 프롬프트
      autoCreateStyle?: boolean; // 여러 스타일 이미지로 자동 스타일 생성
      response_format?: 'url' | 'b64_json';
    } = {}
  ) {
    if (strength < 0 || strength > 1) {
      throw new Error('강도는 0과 1 사이의 값이어야 합니다.');
    }

    if (options.n && (options.n < 1 || options.n > 6)) {
      throw new Error('생성할 이미지 수는 1-6 사이여야 합니다.');
    }

    if (options.styleImages && options.styleImages.length > 5) {
      throw new Error('스타일 이미지는 최대 5개까지 가능합니다.');
    }

    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('prompt', prompt);
    formData.append('strength', strength.toString());
    
    // 스타일 이미지들 추가
    if (options.styleImages && options.styleImages.length > 0) {
      options.styleImages.forEach(styleImage => {
        formData.append('style_images', styleImage);
      });
    }
    
    // 다른 옵션들 추가
    if (options.style) {
      formData.append('style', options.style);
    }
    
    if (options.styleId) {
      formData.append('style_id', options.styleId);
    }
    
    if (options.n) {
      formData.append('n', options.n.toString());
    }
    
    if (options.model) {
      formData.append('model', options.model);
    }
    
    if (options.negativePrompt) {
      formData.append('negative_prompt', options.negativePrompt);
    }
    
    if (options.autoCreateStyle !== undefined) {
      formData.append('auto_create_style', options.autoCreateStyle.toString());
    }
    
    formData.append('response_format', options.response_format || 'url');

    const response = await API.post('/api/ai/recraft/image-to-image', formData);
    
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지-투-이미지 변환에 실패했습니다.');
    }
  }

  /**
   * Recraft 서비스 상태 확인
   * @returns 서비스 상태
   */
  async healthCheck() {
    const response = await API.get('/api/ai/recraft/health');
    
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '헬스 체크에 실패했습니다.');
    }
  }

}